import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, UserCheck } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { client_type: "particulier", first_name: "", last_name: "", company: "", email: "", phone: "", address: "" };

// Création rapide d'une vraie fiche client sans quitter le formulaire en cours.
export default function QuickClientDialog({ open, setOpen, onCreated }) {
  const [f, setF] = useState(EMPTY);
  const [dups, setDups] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); setDups(null); };
  const close = (o) => { setOpen(o); if (!o) { setF(EMPTY); setDups(null); } };

  const create = async () => {
    const pro = f.client_type === "professionnel";
    if (!(f.last_name.trim() || f.first_name.trim() || (pro && f.company.trim()))) return toast.error("Indiquez au moins un nom ou une société");
    if (f.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim())) return toast.error("E-mail invalide");
    setBusy(true);
    try {
      if (!dups) {
        const { data } = await api.get("/clients/check-duplicates", { params: { email: f.email, phone: f.phone, first_name: f.first_name, last_name: f.last_name, company: pro ? f.company : "" } });
        if (data.length) { setDups(data); return; }
      }
      const { data } = await api.post("/clients", { ...f, email: f.email.trim() || null, company: f.company.trim() || null, phone: f.phone.trim() || null, address: f.address.trim() || null });
      toast.success("Client créé");
      onCreated(data);
      close(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const pickExisting = (c) => { onCreated({ id: c.id, _existing: true }); close(false); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-white border-slate-200 max-w-md max-h-[90vh] overflow-y-auto" data-testid="quick-client-dialog">
        <DialogHeader><DialogTitle className="text-slate-900">Nouveau client</DialogTitle><DialogDescription>Le devis en cours est conservé.</DialogDescription></DialogHeader>
        <div className="flex gap-2">
          {[["particulier", "Particulier"], ["professionnel", "Professionnel"]].map(([k, l]) => (
            <button key={k} type="button" data-testid={`qc-type-${k}`} onClick={() => setF({ ...f, client_type: k })}
              className={`flex-1 rounded-lg border px-3 py-2 text-sm ${f.client_type === k ? "border-emerald-500 bg-emerald-50 text-emerald-800 font-semibold" : "border-slate-200 text-slate-600"}`}>{l}</button>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {f.client_type === "professionnel" && <Field label="Société" className="sm:col-span-2"><Input value={f.company} onChange={set("company")} data-testid="qc-company" /></Field>}
          <Field label="Prénom"><Input value={f.first_name} onChange={set("first_name")} data-testid="qc-first-name" /></Field>
          <Field label="Nom"><Input value={f.last_name} onChange={set("last_name")} data-testid="qc-last-name" /></Field>
          <Field label="E-mail"><Input type="email" inputMode="email" value={f.email} onChange={set("email")} data-testid="qc-email" /></Field>
          <Field label="Téléphone"><Input type="tel" inputMode="tel" value={f.phone} onChange={set("phone")} data-testid="qc-phone" /></Field>
          <Field label="Adresse" className="sm:col-span-2"><Input value={f.address} onChange={set("address")} data-testid="qc-address" /></Field>
        </div>
        {dups && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="qc-duplicates">
            <p className="font-semibold text-amber-800 mb-2">Client(s) similaire(s) déjà enregistré(s) :</p>
            {dups.map((c) => (
              <button key={c.id} type="button" onClick={() => pickExisting(c)} data-testid={`qc-use-${c.id}`} className="w-full flex items-center gap-2 text-left rounded-md bg-white border border-amber-200 px-2 py-1.5 mb-1 hover:bg-amber-100">
                <UserCheck className="w-4 h-4 text-amber-700 shrink-0" /><span className="min-w-0 break-words"><b>{c.name || "—"}</b>{c.email ? ` · ${c.email}` : ""}{c.phone ? ` · ${c.phone}` : ""}</span>
              </button>
            ))}
            <p className="text-xs text-amber-700 mt-1">Touchez un client pour l'utiliser, ou « Créer quand même ».</p>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => close(false)} data-testid="qc-cancel">Annuler</Button>
          <Button onClick={create} disabled={busy} data-testid="qc-save" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">{busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}{dups ? "Créer quand même" : "Créer le client"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, className = "", children }) {
  return <div className={`space-y-1.5 ${className}`}><Label className="text-xs text-slate-500">{label}</Label>{children}</div>;
}
